import { t } from '@lingui/macro'
import { ExplorerTasksService, OpenAPI, TaskDTO } from '@om1/falcon-api'
import { handleApiError } from '@om1/falcon-api/utils'
import { notificationActions } from '@om1/platform-notifications'
import axios from 'axios'
import { EventChannel } from 'redux-saga'
import { call, put, take } from 'redux-saga/effects'
import { renderCohortDuplicateSuccessToast } from '../components/list/CohortDuplicateSuccessToast'
import { CohortRoutes } from '../routes'
import { cohortListActions } from '../state'
import { taskSocket } from './task-socket'

export function createDuplicateCohortSaga() {
    return function* (action: ReturnType<typeof cohortListActions.cohortDuplicate>) {
        const { cohortId, shouldUnmarkFeatured } = action.payload

        yield put(cohortListActions.loadingSet({ loading: true }))

        let taskId
        try {
            const taskDTO: TaskDTO = yield call(ExplorerTasksService.cohortDuplicateExplorerTasksCohortCohortIdDuplicateGet, {
                cohortId: cohortId
            })
            taskId = taskDTO.taskId
        } catch (e) {
            console.log(e)
            yield put(notificationActions.error(t`Could not get task id`))
            yield put(cohortListActions.loadingSet({ loading: false }))
        }

        if (taskId) {
            try {
                const eventChannel: EventChannel<any> = yield call(taskSocket, taskId, OpenAPI.TOKEN)
                const socketResponse = JSON.parse(yield take(eventChannel)) as {
                    taskId: string
                    status: string
                    result: { cohortId: string; cohortName: string } & { error?: string }
                }
                if (socketResponse.status === 'SUCCESS') {
                    const newCohortId = socketResponse.result.cohortId

                    // If the original cohort was featured, unmark the duplicate as featured
                    if (shouldUnmarkFeatured) {
                        try {
                            // Use direct axios call to handle the false value (same workaround as in the card)
                            const url = `${OpenAPI.BASE}/explorer/cohorts/${newCohortId}/feature`

                            const headers: Record<string, string> = {
                                'Content-Type': 'application/json'
                            }

                            const requestOptions = {
                                method: 'PATCH' as const,
                                url: `/explorer/cohorts/${newCohortId}/feature`,
                                body: false
                            }

                            if (OpenAPI.TOKEN) {
                                const token = typeof OpenAPI.TOKEN === 'string' ? OpenAPI.TOKEN : yield call(OpenAPI.TOKEN, requestOptions)
                                // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
                                headers['Authorization'] = `Bearer ${token}`
                            }

                            if (OpenAPI.HEADERS) {
                                const additionalHeaders =
                                    typeof OpenAPI.HEADERS === 'function' ? yield call(OpenAPI.HEADERS, requestOptions) : OpenAPI.HEADERS
                                Object.assign(headers, additionalHeaders)
                            }

                            yield call(axios.patch, url, false, {
                                headers,
                                withCredentials: OpenAPI.WITH_CREDENTIALS
                            })
                        } catch (error) {
                            // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
                            console.error('Failed to unmark duplicated cohort as featured:', error)
                            // Don't fail the entire duplication process if unmarking featured fails
                        }
                    }

                    window.location.href = `/${CohortRoutes.COHORTBUILD}/${CohortRoutes.DASHBOARD}/${CohortRoutes.CUSTOM}/${newCohortId}`
                    yield put(
                        notificationActions.success(renderCohortDuplicateSuccessToast({ id: newCohortId, name: socketResponse.result.cohortName }))
                    )
                } else if (socketResponse.status === 'FAILURE' && (socketResponse.result.error || '').includes('TimeoutError')) {
                    yield handleApiError(socketResponse.result.error)
                } else {
                    yield put(notificationActions.error(t`Could not duplicate the cohort`))
                }
            } catch (error) {
                yield handleApiError(error)
            }
        }

        yield put(cohortListActions.loadingSet({ loading: false }))
    }
}
